import classNames from 'classnames';
import * as React from 'react';
import type { ReactNode } from 'react';
import { AlertSectionVariant } from '~/Blocks/AlertSectionBlock/AlertSectionVariant.csharp';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import type { Theme } from '~/Utils/Theme';
import styles from './AlertSection.module.css';
import type { AlertSectionSizeVariant } from './AlertSectionSizeVariant';

export interface AlertSectionProps {
    variant: AlertSectionVariant;
    sizeVariant?: AlertSectionSizeVariant;
    header?: string;
    headerType?: keyof Pick<React.ReactHTML, 'h1' | 'h2' | 'h3' | 'h4'>;
    children?: ReactNode;
    className?: string;
    headerClassName?: string;
    theme?: Theme;
}

export const AlertSection: React.FC<React.PropsWithChildren<AlertSectionProps>> = props => {
    const contextualTheme = useTheme();
    const {
        variant,
        sizeVariant = 'large',
        header,
        children,
        className,
        headerClassName,
        headerType = 'h2',
        theme = contextualTheme,
    } = props;

    const { withRoundedCorners } = theme;

    const getIconName = () => {
        switch (variant) {
            case AlertSectionVariant.Success:
                return 'check-circle';
            case AlertSectionVariant.Warning:
                return 'triangle';
            case AlertSectionVariant.Error:
                return 'error-sign';
            default:
                return 'info-circle';
        }
    };

    return (
        <div
            className={classNames(
                styles.AlertSection,
                styles[`Variant--${variant}`],
                styles[`Variant--${sizeVariant}`],
                !header && styles['Variant--noHeader'],
                withRoundedCorners && styles['Variant--rounded'],
                className
            )}
        >
            <SvgIcon iconName={getIconName()} className={styles.Icon} />
            {header &&
                React.createElement(headerType, { className: classNames(styles.Header, headerClassName) }, header)}
            {children && <div className={styles.Body}>{children}</div>}
        </div>
    );
};
